import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, ViewChild, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { DealCreateInterface } from '@zaoblako/shared/modules/business/dto/deal/deal.create.dto';
import { DealFormDto } from '@zaoblako/shared/modules/business/dto/deal/deal.form.dto';
import type { DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import type { DealTypeFindInterface } from '@zaoblako/shared/modules/business/dto/deal-type/deal-type.find.dto';
import { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';
import { EntityValidatorClass } from '@zaoblako/shared/modules/eav/classes/entity-validator.class';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { VoiceAssistantBridgeService } from '@zaoblako/web/shared/voice-assistant/services/voice-assistant-bridge.service';
import { VoiceAssistantFormEnum } from '@zaoblako/shared/modules/ai/enums/voice-assistant-form.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { DealTypeService } from '@zaoblako/web/core/services/deal/deal-type.service';
import { DealOrderFormComponent } from '@zaoblako/web/modules/deal/dumbs/deal-order-form/deal-order-form.component';
import { FilesListComponent } from '@zaoblako/web/shared/components/files-list/files-list.component';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { CostCenterMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-multi-select/cost-center-multi-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-multi-select/filial-multi-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { HdTaskMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-multi-select/hd-task-multi-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { PrioritySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/priority-single-select/priority-single-select.component';
import { ProjectMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/project-multi-select/project-multi-select.component';
import { RoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-multi-select/room-multi-select.component';
import { RoomSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-single-select/room-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { EavComputedExpressionPipe } from '@zaoblako/web/shared/pipes/eav-computed-expression.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { ContractSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contract-single-select/contract-single-select.component';
import { ExternalUserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-single-select/external-user-single-select.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { Subscription } from 'rxjs';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { DealRentFormComponent } from '@zaoblako/web/modules/deal/dumbs/deal-rent-form/deal-rent-form.component';

@Component({
  standalone: true,
  selector: 'crm-deal-form',
  templateUrl: './deal-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    OrderByPipe,
    EavComputedExpressionPipe,
    InputNumberModule,
    CheckboxModule,
    CostCenterMultiSelectComponent,
    CostCenterSingleSelectComponent,
    SingleSelectComponent,
    ContragentAutoCompleteComponent,
    EditorComponent,
    ButtonComponent,
    GoBackButtonComponent,
    FormsModule,
    MultiSelectComponent,
    DirectionSingleSelectComponent,
    FilesListComponent,
    FileUploadModule,
    PrioritySingleSelectComponent,
    HdTaskMultiSelectComponent,
    ProjectMultiSelectComponent,
    UnitSingleSelectComponent,
    FilialMultiSelectComponent,
    FilialSingleSelectComponent,
    RoomMultiSelectComponent,
    RoomSingleSelectComponent,
    DealOrderFormComponent,
    UserMultiSelectComponent,
    UserGroupMultiSelectComponent,
    DatepickerComponent,
    UserSingleSelectComponent,
    DividerModule,
    ContractSingleSelectComponent,
    ExternalUserSingleSelectComponent,
    CompanySingleSelectComponent,
    DaterangepickerComponent,
    DealRentFormComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealFormComponent extends BaseFormComponent<DealCreateInterface> implements OnInit, OnDestroy {
  @Input() public deal: DealCreateInterface = {
    name: '',
    orders: [],
    contragent: null
  };

  public dto = DealFormDto;

  @ViewChild('externalUserSingleSelectComponent')
  public readonly externalUserSingleSelectComponent: ExternalUserSingleSelectComponent;

  @Input() public groups: DealsGroupFindInterface[] = [];
  @Input() public directions: DirectionFindInterface[] = [];
  @Input() public tasks: HelpdeskTaskFindInterface[] = [];
  @Input() public projects: ProjectFindInterface[] = [];

  public dealTypes: DealTypeFindInterface[] = [];

  @ViewChild('fileUpload') public fileUpload: FileUpload;
  public filesLoading = false;

  private formSubscription$: Subscription = new Subscription();
  private voiceAssistantContragentSubscription$: Subscription;
  private voiceAssistantContractSubscription$: Subscription;

  public constructor(
    public readonly globalService: GlobalService,
    public readonly accessService: AccessService,
    private readonly voiceAssistantBridgeService: VoiceAssistantBridgeService,
    private readonly confirmService: ConfirmDialogService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly fileService: FileService,
    private readonly formStoreService: FormStoreService,
    private readonly dealTypeService: DealTypeService
  ) {
    super();
    this.loadDealTypes();
  }

  public ngOnInit(): void {
    this.voiceAssistantContragentSubscription$ = this.voiceAssistantBridgeService
      .onFormSave(VoiceAssistantFormEnum.CONTRAGENT)
      .subscribe((contragent) => this.confirmService.confirm({
        message: `Назначить контрагента "${contragent?.name}" данной сделке?`,
        accept: () => {
          this.deal.contragent = contragent;
          this.cdRef.markForCheck();
        }
      }));
    this.voiceAssistantContractSubscription$ = this.voiceAssistantBridgeService
      .onFormSave(VoiceAssistantFormEnum.CONTRACT)
      .subscribe((contract) => this.confirmService.confirm({
        message: `Назначить договор №${contract?.number} "${contract?.name}" данной сделке?`,
        accept: () => {
          this.deal.contract = contract;
          this.cdRef.markForCheck();
        }
      }));
  }

  public ngOnDestroy() {
    this.formSubscription$?.unsubscribe();
    this.voiceAssistantContragentSubscription$?.unsubscribe();
    this.voiceAssistantContractSubscription$?.unsubscribe();
  }

  public get EavAttributeValueTypeEnum() {
    return EavAttributeValueTypeEnum;
  }

  public onDealTypeChange(dealType: DealTypeFindInterface | null) {
    this.deal.dealType = dealType ?? null;

    this.setDefaultFieldsFromDealType(dealType);
    this.setEntityTypeFromDealType(dealType);
  }

  public override onSave(deal: DealCreateInterface) {
    if (deal?.dealType?.eavEntityType?.attributes?.length && deal?.eavEntity?.values) {
      if (Object.keys(deal.eavEntity.values).every((key) => !deal.eavEntity.values[key])) {
        deal.eavEntity.values = {};
      }

      const validator = EntityValidatorClass.getInstance().createSafeSyncValidator(
        deal.dealType.eavEntityType.attributes,
        {
          transformValidationErrors: (values) => ({ eavEntity: { values } }),
          transformValidationMetadata: (nested) => ({
            eavEntity: {
              label: 'Дополнительные реквизиты',
              nested: {
                values: {
                  label: 'Значения',
                  nested
                }
              }
            }
          })
        }
      );

      const [, errors] = validator(deal.eavEntity.values);

      if (errors?.validationErrors) {
        this.errors = errors.validationErrors as any;
        return;
      }
    }

    super.onSave(deal);
  }

  private readonly loadDealTypes = () => {
    this.dealTypeService
      .findPage({
        limit: 100,
        offset: 0,
        withDeleted: false
      })
      .subscribe((dealTypePage) => {
        this.dealTypes = dealTypePage.items;

        if (this.deal?.dealType?.id) {
          const currentDealType = this.dealTypes.find((dealType) => dealType.id === this.deal.dealType.id);
          if (currentDealType) {
            this.deal.dealType = currentDealType;
          }
        } else if (this.dealTypes?.length === 1 && !this.canDelete) {
          this.deal.dealType = this.dealTypes[0];
        }

        this.cdRef.markForCheck();
      });
  };

  public uploadFiles(data: FileUploadHandlerEvent) {
    this.filesLoading = true;
    this.cdRef.markForCheck();

    this.fileService.upload(data.files).subscribe({
      next: (files) => {
        this.deal.files = this.deal.files?.length ? this.deal.files.concat(files) : files;
        this.fileUpload.clear();
        this.filesLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.filesLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public deleteFile(files: FileFindInterface[], file: FileFindInterface) {
    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить файл "${file.name}"?`,
      accept: () => {
        const index = files.indexOf(file);
        files.splice(index, 1);
        this.cdRef.markForCheck();
      }
    });
  }

  public changeContragent() {
    if (this.deal.externalUser) {
      this.deal.externalUser = null;
    }
  }

  public openFormCreateExternalUser() {
    const result = this.formStoreService.addForm(TypeFormEnum.EXTERNAL_USER_CREATE, {
      headerText: 'Добавление внешнего пользователя',
      widthSize: 'form-sidebar-small',
      data: {
        client: this.deal.contragent.client,
        contragent: this.deal.contragent
      }
    });

    this.formSubscription$.add(
      result.onSave.subscribe((externalUser: ExternalUserFindInterface) => {
        this.externalUserSingleSelectComponent.firstLoad();
        this.deal.externalUser = externalUser;
        this.cdRef.markForCheck();
      })
    );
  }

  private readonly setDefaultFieldsFromDealType = (dealType: DealTypeFindInterface | null) => {
    if (!dealType) {
      return;
    }

    this.deal.company = null;
    this.deal.responsible = null;
    this.deal.userAuditors = [];
    this.deal.groupAuditors = [];

    if (dealType.defaultCompany) {
      this.deal.company = dealType.defaultCompany;
    }

    if (dealType.responsible) {
      this.deal.responsible = dealType.responsible;
    }

    if (dealType.auditorUsers?.length) {
      this.deal.userAuditors = dealType.auditorUsers;
    }

    if (dealType.auditorGroups?.length) {
      this.deal.groupAuditors = dealType.auditorGroups;
    }
  };

  private readonly setEntityTypeFromDealType = (dealType: DealTypeFindInterface | null) => {
    const attributes = dealType?.eavEntityType?.attributes;

    if (!attributes?.length) {
      this.deal.eavEntity = null;
      this.cdRef.markForCheck();
      return;
    }

    const values = attributes.reduce(
      (accumulator, attribute) => {
        const currentValues = this.deal.eavEntity?.values;
        accumulator[attribute.id] = currentValues ? currentValues[attribute.id] ?? null : null;
        return accumulator;
      },
      {} as Record<number, unknown>
    );

    this.deal.eavEntity = { values };
    this.cdRef.markForCheck();
  };
}
